import { useEffect, useRef } from 'react';
import { isValidRef } from '../../helpers/isValidRef';
import { noOpFn } from '../../helpers/noOpFn';

type AddEventListenerType = (
  type: string,
  listener: (evt: Event) => void,
  options?: AddEventListenerOptions | boolean,
) => void;

type RemoveEventListenerType = (
  type: string,
  listener: (evt: Event) => void,
  options?: AddEventListenerOptions | boolean,
) => void;

type Listenable = {
  addEventListener: AddEventListenerType;
  removeEventListener: RemoveEventListenerType;
};

// ensure element supports addEventListener
const isEventTargetSupported = (eventTarget: unknown): eventTarget is Listenable =>
  // @ts-expect-error the compiler thinks that addEventListener may not exist on eventTarget, but we still need to check it.

  Boolean(typeof eventTarget === 'object' && eventTarget?.addEventListener);

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const useEvent = <T extends Event>(
  eventName: Parameters<AddEventListenerType>[0],
  eventHandler: (event: T) => unknown,
  eventTarget: unknown = window,
  eventOptions: Parameters<RemoveEventListenerType>[2] = {},
) => {
  // refs to store eventHandler & eventOptions
  const savedEventHandler = useRef<typeof eventHandler | undefined>(undefined);
  const savedEventOptions = useRef<typeof eventOptions | undefined>(undefined);

  // update ref when handler changes;
  // avoids effect dependencies array which could cause effect to re-run every render
  useEffect(() => {
    savedEventHandler.current = eventHandler;
  }, [eventHandler]);

  // update ref when options changes
  useEffect(() => {
    savedEventOptions.current = eventOptions;
  }, [eventOptions]);

  useEffect(() => {
    const target = isValidRef(eventTarget) ? eventTarget.current : eventTarget;

    // early return if we can't do anything useful with this hook
    if (!eventName || !isEventTargetSupported(target)) {
      return;
    }

    const eventListener = (event: Event) => {
      if (savedEventHandler.current !== undefined) {
        return savedEventHandler.current(event as T);
      }
      return noOpFn;
    };

    target.addEventListener(eventName, eventListener, savedEventOptions.current);

    // remove event listener on unmount
    // eslint-disable-next-line consistent-return
    return () => {
      target.removeEventListener(eventName, eventListener, savedEventOptions.current);
    };
  }, [eventName, eventTarget, savedEventOptions]);
};
